import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type Ref,
  type CSSProperties,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import useVirtual from "react-cool-virtual";

import { Icon } from "@/components/atoms/Icon";
import { useColorScheme } from "@/hooks/useColorScheme";
import { useDevice } from "@/hooks/useDevice";
import {
  SelectableList,
  useSelectableListStyle,
  type SelectableListBaseProps,
  type SelectableListContext,
} from "@/components/molecules/SelectableList";
import { icons } from "@/objects/@icons";
import { buttonVariants } from "@/components/ui/button";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { SELECTABLE_LIST_ITEM_ROW_HEIGHT } from "./SelectableListItemRow";

import {
  SELECTABLE_CARD_RADIUS,
  SELECTABLE_CARD_RADIUS_CLASS,
  SELECTABLE_LIST_INSET,
  SELECTABLE_LIST_INSET_CLASS,
  SELECTABLE_LIST_STYLE_DEFAULT,
  SELECTABLE_ROW_GROUND_VAR,
  SELECTABLE_ROW_INSET_CLASS,
  type SelectableRowHover,
} from "./SelectableRow";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

// =============================================================================
// SelectableCardList
// =============================================================================
// Virtualized card rows with hairline dividers — used by Triage, Threads,
// and Workflow Executions. Optional ``groups`` render section headers and
// support per-group single vs multi selection behavior on Space.
// =============================================================================

export type SelectableCardListSelectionMode = "single" | "multi";

export type SelectableCardListSection<T> = {
  id: string;
  label: string;
  /** When set, rendered instead of the plain ``label`` string (e.g. breadcrumb overline). */
  labelContent?: ReactNode;
  subtitle?: string;
  items: T[];
};

export type SelectableCardListGroup<T> = {
  id: string;
  label: string;
  /** When set, rendered instead of the plain ``label`` string (e.g. breadcrumb overline). */
  labelContent?: ReactNode;
  /** Trailing control in the sticky group header (e.g. help tooltip). */
  labelEnd?: ReactNode;
  subtitle?: string;
  items: T[];
  /** When set, renders a sticky rule sub-header per section instead of a flat item list. */
  sections?: SelectableCardListSection<T>[];
  selectionMode: SelectableCardListSelectionMode;
  emptyLabel?: string;
};

export type SelectableCardListLoadMore = {
  hasMore: boolean;
  loading?: boolean;
  onLoadMore: () => void;
  /**
   * How many rows the page being read brings, when the list knows - the
   * placeholder rows drawn after those in hand while it loads. Unsaid, as
   * many as the list draws loading (``loadingItemCount``).
   */
  loadingItemCount?: number;
};

function SelectableCardListLoadMoreSentinel({
  loadMore,
  scrollRootRef,
  flush,
}: {
  loadMore: SelectableCardListLoadMore;
  scrollRootRef: React.RefObject<HTMLElement | null>;
  flush: boolean;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(loadMore.onLoadMore);
  onLoadMoreRef.current = loadMore.onLoadMore;

  useEffect(() => {
    if (!loadMore.hasMore || loadMore.loading) {
      return;
    }
    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root, rootMargin: "200px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore.hasMore, loadMore.loading, scrollRootRef]);

  if (!loadMore.hasMore && !loadMore.loading) {
    return null;
  }

  return (
    <div
      ref={sentinelRef}
      className={cn(flush ? "pl-0 pr-0" : "pl-1 pr-1", "pt-4 pb-4 min-h-8")}
    />
  );
}

export type SelectableCardListRenderItemArgs<T> = {
  /**
   * The row's item - always one: while the list loads it is the list's
   * ``placeholderItem``, drawn as any row is.
   */
  item: T;
  index: number;
  isActive: boolean;
  /** True while keyboard focus is within the list scope. */
  isFocusWithin: boolean;
  group?: SelectableCardListGroup<T>;
  /**
   * The row stands in for one while the list loads. It draws the tree it
   * always draws, its pieces wrapped in ``Skeleton`` (``loading``), so
   * each bar is the size of what it stands for - never a separate skeleton.
   */
  isLoading: boolean;
  /**
   * In a list given ``renderItemDetails``: whether this row is open, whether
   * it has anything to open to, and what opens or shuts it - for the row's own
   * control (a chevron, say). The list keeps which rows are open.
   */
  isOpen?: boolean;
  canOpen?: boolean;
  toggleOpen?: () => void;
};

/**
 * Something a row does when it is swiped: on a touch screen the row slides
 * aside under the finger and these are behind it, at its end.
 */
export type SelectableCardListSwipeAction = {
  key: string;
  label: string;
  icon: ReactNode;
  variant?: "default" | "destructive";
  onAction: () => void;
};

/** How wide each swipe action is: a thumb's target. */
const SWIPE_ACTION_WIDTH = 80;
/** How far a finger goes before the list knows it is a swipe or a scroll. */
const SWIPE_SLOP = 8;

export type SelectableCardListProps<T> = Omit<
  SelectableListBaseProps<T>,
  "items"
> & {
  items?: T[];
  groups?: SelectableCardListGroup<T>[];
  /** Estimated row height in px; the virtualizer measures real heights. */
  estimatedItemHeight?: number;
  /** Larger estimate for the active row when ``variableItemHeight`` is set. */
  activeItemHeight?: number;
  /** Let rows size to content; uses ``measureRef`` instead of a fixed min-height. */
  variableItemHeight?: boolean;
  /** Render all rows in normal document flow (no absolute positioning). */
  disableVirtualization?: boolean;
  /** Hairline dividers between rows (off for padded card rows). */
  showRowDividers?: boolean;
  /**
   * ``between`` (default): line above every row after the first.
   * ``overline``: line above every row, including the first.
   */
  rowDividerPlacement?: "between" | "overline";
  /**
   * What rows do under the pointer. ``surface`` (default): the row takes a
   * surface and the hairlines beside it step aside. ``title``: the row's
   * title is underlined and nothing else moves - surface and hairlines stay.
   */
  rowHover?: SelectableRowHover;
  /** Fetch the next page when the sentinel scrolls into view. */
  loadMore?: SelectableCardListLoadMore;
  /** Keep group section labels pinned while scrolling (grouped layout only). */
  stickyGroupLabels?: boolean;
  /** Height of sticky group label bars (defaults to 48). */
  stickyGroupLabelHeight?: number;
  /** Height of sticky rule sub-headers nested under group labels. */
  stickySectionLabelHeight?: number;
  /**
   * Pin the active row within the scrollport (top or bottom) via CSS
   * ``position: sticky``. Requires ``disableVirtualization`` (or grouped layout).
   * The gap and padding left bare round it are covered in whatever the list
   * stands on, read off the page; a row taller than the scrollport is left to
   * scroll, since pinned its end could never be reached.
   */
  stickyActiveRow?: boolean;
  /**
   * Rows taken out of the list fold away - fading as they close up - rather
   * than vanish. Requires ``disableVirtualization``.
   */
  animatesRemovals?: boolean;
  /**
   * The rows it is given now fill in round a screenful drawn first: those
   * added are simply there, not slid in as rows newly put in are.
   */
  isFillingIn?: boolean;
  /**
   * What the rows are a list of - its search, its filters. A row taken out
   * of the same list folds away (``animatesRemovals``); one a list of
   * something else leaves out goes at once, as do those giving way to
   * placeholders.
   */
  listKey?: string;
  /** Top inset when pinned. */
  stickyActiveRowInsetTop?: number;
  /** Bottom inset when the active row is pinned. */
  stickyActiveRowInsetBottom?: number;
  /** Report which group is pinned at the top of the scrollport. */
  onVisibleGroupChange?: (groupId: string) => void;
  /** Scroll this group flush with the top of the list. */
  scrollToGroupId?: string | null;
  /** Increment to re-run scroll when ``scrollToGroupId`` is unchanged. */
  scrollToGroupKey?: number;
  /** Inset around each group's items (sticky headers stay full width). */
  groupItemsClassName?: string;
  renderItem: (args: SelectableCardListRenderItemArgs<T>) => ReactNode;
  /**
   * What a row opens to. Given it, the list's rows are accordions: the list
   * keeps which are open and hands each row ``isOpen`` / ``canOpen`` /
   * ``toggleOpen`` (``renderItem`` draws the head, and its own control). A row
   * this gives nothing for has nothing to open. Open, the row's head stays in
   * sight while this scrolls under it - held where a pinned row stops, over a
   * covered strip - and is drawn as the card's own top, so the card's border
   * and corners stay whole round it. Reading it is not choosing the row.
   * Requires ``disableVirtualization`` and no ``groups``.
   */
  renderItemDetails?: (args: {
    item: T;
    index: number;
    isActive: boolean;
  }) => ReactNode;
  /**
   * Which rows are picked, in a list of accordions whose rows can be - not the
   * row the keyboard is on (``isItemActive``), but the one a click, Enter or a
   * checkbox chose. Given it, a row opens as it is picked and shuts as it is
   * let go, however that happened; its own control opens and shuts it without
   * either.
   */
  isItemSelected?: (item: T, index: number) => boolean;
  /**
   * What the rows are while the list loads: this item, ``loadingItemCount``
   * times, drawn by ``renderItem`` - the same row, card and size as any other
   * - with ``isLoading``. There is no other way to draw a loading row, so one
   * cannot come out a different size from the rows it stands for. Give it a
   * typical row's shape: text of a typical length, the parts a row has.
   */
  placeholderItem: T;
  /** How many placeholder rows while ``loading`` (defaults to 3). */
  loadingItemCount?: number;
  /**
   * What a row does when it is swiped on a touch screen (``null`` or none:
   * it is not swiped). A swipe toward the row's start shows them at its end;
   * one is tapped, or the row is tapped or swiped back, to put it back.
   */
  getItemSwipeActions?: (item: T) => SelectableCardListSwipeAction[] | null;
  /** Classes on each row's clickable wrapper, over what its variant draws. */
  rowClassName?: string;
  /** Classes on the chosen row's wrapper, over ``rowClassName``. */
  activeRowClassName?: string;
  /** Classes on the list's scroll container. */
  className?: string;
};

const DEFAULT_ESTIMATED_ITEM_HEIGHT = SELECTABLE_LIST_ITEM_ROW_HEIGHT;

function handleSelectableCardRowClickCapture(
  event: MouseEvent<HTMLElement>,
  /** Receives ``event.detail``: 2 on the second click of a double-click. */
  onActivate: (clickCount: number) => void,
) {
  const target = event.target;
  // Clicks in a portaled overlay opened from the row (e.g. a "…" menu)
  // propagate through React to the row, but aren't clicks on it.
  if (target instanceof Node && !event.currentTarget.contains(target)) {
    return;
  }
  if (target instanceof Element) {
    // A control of its own, or a part of the row that says it is for reading
    // (``data-selectable-row-inert``: what a row opens to), is not the row.
    const interactive = target.closest(
      'button, a[href], input, select, textarea, [role="button"], [contenteditable="true"], [data-selectable-row-inert]',
    );
    if (interactive && event.currentTarget.contains(interactive)) {
      return;
    }
  }
  onActivate(event.detail);
}

const DEFAULT_LOADING_ITEM_COUNT = 3;

/** The list's scroll container: it scrolls, its scrollbar drawn over it, not beside. */
const SCROLL_CONTAINER_CLASS =
  "flex-1 min-h-0 overflow-auto w-full [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden";

/** Round each group's items. */
const GROUP_ITEMS_CONTAINER_CLASS = "p-0.5";

/** A row's inset and corners, from the list's size and layout. */
function useSelectableCardRowStyle() {
  const { size, layout, rowChromeless } = useSelectableListStyle();
  return {
    size,
    // A row that draws itself has its own inset; the wrapper adds none.
    rowInsetClassName: rowChromeless
      ? ""
      : SELECTABLE_ROW_INSET_CLASS[layout][size],
    // As a value, for what is worked out from it: the stock radius scale
    // (globals.css), which Tailwind writes into its classes, not as variables.
    rowRadius:
      layout === "cards"
        ? {
            sm: "calc(var(--radius) - 4px)",
            md: "calc(var(--radius) - 2px)",
            lg: "var(--radius)",
            xl: "calc(var(--radius) + 4px)",
          }[SELECTABLE_CARD_RADIUS[size]]
        : 0,
    rowRadiusClassName:
      layout === "cards"
        ? SELECTABLE_CARD_RADIUS_CLASS[SELECTABLE_CARD_RADIUS[size]]
        : "rounded-none",
  };
}

/**
 * Hide hairlines adjacent to a hovered row (same adjacency rules as
 * selection): the hairline in a ``data-selectable-row`` whose
 * ``data-selectable-row-body`` is hovered, and in the row after it.
 */
const SELECTABLE_CARD_ROW_DIVIDER_HOVER_CLASS =
  "[&_[data-selectable-row]:has([data-selectable-row-body]:hover)_[data-selectable-row-divider]]:bg-transparent [&_[data-selectable-row]:has([data-selectable-row-body]:hover)+[data-selectable-row]_[data-selectable-row-divider]]:bg-transparent";

function assignRef<T>(ref: Ref<T> | undefined, value: T) {
  if (!ref) {
    return;
  }
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  ref.current = value;
}

/**
 * How far what covers the strip round a held row reaches in under that row,
 * past the line it is held on: so no seam shows between them, and nothing
 * shows round a card's rounded corners. Under the row, so it cuts none of the
 * row's own line or lift.
 */
export const HELD_ROW_COVER_REACH = 12;

/**
 * And how far past the list's edge it sticks: the scrolled rows and what
 * sticks over them are rounded to the pixel apart, and a cover held exactly
 * at the edge left a pixel's sliver of the rows along it. Past the edge the
 * list clips it, so there is none.
 */
export const HELD_ROW_COVER_OVERHANG = 2;

/** On a row whose open end is coming up under its held head. */
const SELECTABLE_ROW_END_HELD_ATTR = "data-selectable-row-end-held";

/**
 * How far before an open row's end reaches its held head the end is drawn
 * over the head: past what one frame's scroll moves it.
 */
const HELD_ROW_END_LEAD = 64;

/**
 * What an open row opened to, growing into place and shrinking out of it.
 *
 * It is mounted shut and opened a render later - a height has to be laid out
 * before it can be moved from - and, shut, it stays until it has shrunk away,
 * then says so (``onShut``) for the list to let it go.
 */
function SelectableCardListRowDetails({
  expanded,
  onShut,
  className,
  children,
}: {
  expanded: boolean;
  onShut: () => void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(false);
  useLayoutEffect(() => {
    if (expanded === shown) return;
    if (expanded) {
      ref.current?.getBoundingClientRect();
      setShown(true);
      return;
    }
    setShown(false);
    // Nothing moves for someone who has asked for no motion, nor where there
    // is no height to shrink - what it opened to not drawn yet - so no
    // transition ends to say so: gone at once.
    if (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !ref.current?.getBoundingClientRect().height
    ) {
      onShut();
    }
  }, [expanded, shown, onShut]);

  return (
    <div
      ref={ref}
      data-selectable-row-details=""
      // Reading what a row opened to is not choosing it.
      data-selectable-row-inert=""
      onTransitionEnd={(event) => {
        if (
          event.target === ref.current &&
          event.propertyName === "grid-template-rows" &&
          !expanded
        ) {
          onShut();
        }
      }}
      className={cn(
        "grid",
        shown ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        "transition-normal [transition-duration:240ms] [transition-timing-function:cubic-bezier(0.4,0,0.2,1)] delay-[0s] [transition-property:grid-template-rows] motion-reduce:transition-normal [@media(prefers-reduced-motion:_reduce)]:[transition-duration:0s] [@media(prefers-reduced-motion:_reduce)]:[transition-timing-function:ease] motion-reduce:delay-[0s] motion-reduce:transition-none",
      )}
    >
      {/* Everything that takes room is in here, so shut it is nothing. */}
      <div className="min-h-0 overflow-hidden">
        <div className={cn("min-w-0", className)}>{children}</div>
      </div>
    </div>
  );
}

const ROW_SLIDES_S = 0.22;
const ROW_CLOSES_S = 0.24;
// Taken out, a row slides away and fades, out of the list's flow at once
// (``AnimatePresence mode="popLayout"``), and once it has, the rows after it
// glide up into its place (``layout``); put in, the other way about - the rows
// after it glide down to make its place, then it slides in. Only opacity and
// transforms move, as framer draws them: nothing is laid out again frame by
// frame. Rows there as the list is drawn are simply there (``initial={false}``).
const rowFoldsAway = (
  isClosingUp: boolean,
  rowsKey: string,
  isFillingIn: boolean,
) =>
  ({
    layout: "position",

    // Only rows coming and going move the rest: drawn out of sight and then
    // shown, or the list's height changing round them, they stay put.
    layoutDependency: rowsKey,

    // Filled in round a screenful drawn first, a row is simply there: it is
    // not new to the list, only drawn later.
    initial: isFillingIn ? false : { opacity: 0, x: 48 },

    animate: {
      opacity: 1,
      x: 0,
      transition: {
        delay: ROW_CLOSES_S,
        duration: ROW_SLIDES_S,
        ease: "easeOut",
      },
    },

    exit: {
      opacity: 0,
      x: 48,
      transition: { duration: ROW_SLIDES_S, ease: "easeIn" },
    },

    transition: {
      layout: {
        // Closing up behind rows taken out: once they have slid away.
        delay: isClosingUp ? ROW_SLIDES_S : 0,
        duration: ROW_CLOSES_S,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  }) as const;
/** How long a row taken out takes to fold away (``animatesRemovals``). */
export const SELECTABLE_CARD_LIST_FOLD_MS =
  (ROW_SLIDES_S + ROW_CLOSES_S) * 1000;

function SelectableCardListRowShell({
  index,
  isActive,
  stickyActiveRow,
  stickyActiveRowInsetTop = 0,
  stickyActiveRowInsetBottom = 0,
  registerRowRef,
  scrollInsetTop = 0,
  outerRef,
  wrapperStyle,
  swipeActions,
  animatesRemoval = false,
  isClosingUp = false,
  rowsKey = "",
  isFillingIn = false,
  ref,
  children,
}: {
  index: number;
  isActive: boolean;
  stickyActiveRow: boolean;
  stickyActiveRowInsetTop?: number;
  stickyActiveRowInsetBottom?: number;
  registerRowRef: (index: number, element: HTMLElement | null) => void;
  scrollInsetTop?: number;
  outerRef?: (node: HTMLDivElement | null) => void;
  wrapperStyle?: CSSProperties;
  /** What the row does when swiped on a touch screen. */
  swipeActions?: SelectableCardListSwipeAction[] | null;
  /** Taken out of the list, it folds away (``animatesRemovals``). */
  animatesRemoval?: boolean;
  /** The list has just lost rows: it glides into their place once they have slid away. */
  isClosingUp?: boolean;
  /** Which rows the list has: changed, the rows glide to their new places (``layoutDependency``). */
  rowsKey?: string;
  /** Drawn as the rest of a list drawn a screenful first: there at once, not slid in. */
  isFillingIn?: boolean;
  /** Its wrapper, as ``AnimatePresence`` measures it to take it out of the flow. */
  ref?: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  // Framer draws a row that comes and goes (``animatesRemovals``).
  const RowElement = animatesRemoval ? motion.div : "div";
  const { rowRadiusClassName } = useSelectableCardRowStyle();

  // Swiped: how far the row is slid aside, its actions in the room it leaves.
  // A finger only - a pointer that hovers has the row's own controls - and
  // only across: a finger going down the list scrolls it, as ever.
  const canSwipe = (swipeActions?.length ?? 0) > 0;
  const swipeWidth = (swipeActions?.length ?? 0) * SWIPE_ACTION_WIDTH;
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const swipeRef = useRef<{
    x: number;
    y: number;
    from: number;
    axis: "x" | "y" | null;
  } | null>(null);
  // The click that ends a swipe is the swipe's, not a pick of the row.
  const swallowClickRef = useRef(false);

  // Slid open, it goes back when anything else is touched.
  useEffect(() => {
    if (swipeOffset === 0) return;
    const shut = (event: globalThis.PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setSwipeOffset(0);
      }
    };
    document.addEventListener("pointerdown", shut);
    return () => document.removeEventListener("pointerdown", shut);
  }, [swipeOffset]);

  const swipeHandlers = canSwipe
    ? {
        onPointerDown: (event: PointerEvent<HTMLDivElement>) => {
          // A swipe a phone ended with no click leaves none to swallow.
          swallowClickRef.current = false;
          if (event.pointerType === "mouse") return;
          swipeRef.current = {
            x: event.clientX,
            y: event.clientY,
            from: swipeOffset,
            axis: null,
          };
        },
        onPointerMove: (event: PointerEvent<HTMLDivElement>) => {
          const swipe = swipeRef.current;
          if (!swipe) return;
          const dx = event.clientX - swipe.x;
          const dy = event.clientY - swipe.y;
          if (swipe.axis === null) {
            if (Math.abs(dy) > SWIPE_SLOP && Math.abs(dy) >= Math.abs(dx)) {
              swipeRef.current = null;
              return;
            }
            if (Math.abs(dx) <= SWIPE_SLOP) return;
            // A finger's pointer is the row's until it lifts already.
            swipe.axis = "x";
            setIsSwiping(true);
          }
          setSwipeOffset(
            Math.min(0, Math.max(-swipeWidth * 1.25, swipe.from + dx)),
          );
        },
        onPointerUp: () => {
          const swipe = swipeRef.current;
          swipeRef.current = null;
          if (swipe?.axis !== "x") return;
          swallowClickRef.current = true;
          setIsSwiping(false);
          setSwipeOffset((offset) =>
            offset < -swipeWidth / 2 ? -swipeWidth : 0,
          );
        },
        onPointerCancel: () => {
          swipeRef.current = null;
          setIsSwiping(false);
          setSwipeOffset((offset) =>
            offset < -swipeWidth / 2 ? -swipeWidth : 0,
          );
        },
        onClickCapture: (event: MouseEvent<HTMLDivElement>) => {
          // The swipe's own click, or a tap on a row slid open, which puts it
          // back rather than picking it.
          if (swallowClickRef.current) {
            swallowClickRef.current = false;
          } else if (swipeOffset !== 0) {
            setSwipeOffset(0);
          } else {
            return;
          }
          event.preventDefault();
          event.stopPropagation();
        },
      }
    : null;
  // Whether the row fits the scrollport: a pinned row taller than it would
  // hold its top in place and never let its end come into view.
  const [fits, setFits] = useState(true);
  // Open, a row is mostly what it opened to: pinned whole it would fill the
  // list, or not fit it at all. So only its head is kept in sight - the row is
  // pinned with everything but its head let past the line. At the bottom the
  // head stands on the line and what it opened to goes on under the list's
  // edge; at the top the head holds itself while that scrolls up under it
  // (see the direct body), and the row takes over just as the row's end would
  // push the head off, so the head stays once all of it has gone by.
  const [headPin, setHeadPin] = useState<{
    top: number;
    bottom: number;
  } | null>(null);
  // The list's padding: the strip a pinned row leaves bare at either edge.
  const [pad, setPad] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  // How far into the row its card starts - past the hairline between it and
  // the row over it: pinned, the row is held that much higher, so its card
  // stands where the first row's does, not a hairline under it.
  const [bodyLead, setBodyLead] = useState(0);

  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperRef.current = node;
      registerRowRef(index, node);
      outerRef?.(node);
      assignRef(ref, node);
    },
    [index, outerRef, ref, registerRowRef],
  );

  const wantsSticky =
    stickyActiveRow && isActive && wrapperStyle?.position !== "absolute";
  const useCssSticky = wantsSticky && (headPin != null || fits);
  // Held at the top or the foot as rows come and go round it, it stays where
  // it is held: the list scrolling on under it is not it moving, which its
  // glide (``layout``) would take it to be. Not held, it glides as the rest.
  const heldAcross = useRef({ rowsKey, isHeld: false });
  if (heldAcross.current.rowsKey !== rowsKey) {
    const row = wrapperRef.current;
    let isHeld = false;
    if (useCssSticky && row) {
      const held = row.offsetTop;
      const position = row.style.position;
      row.style.position = "static";
      isHeld = Math.abs(row.offsetTop - held) >= 1;
      row.style.position = position;
    }
    heldAcross.current = { rowsKey, isHeld };
  }
  // What measures the row's pin, for a render to run again: opening a row
  // changes what is pinned before anything is resized.
  const measurePinRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (!wantsSticky) {
      setFits(true);
      setHeadPin(null);
      measurePinRef.current = null;
      return;
    }

    const row = wrapperRef.current;
    const list = row?.parentElement;
    if (!list) {
      return;
    }

    // Only what the row is, never where it has scrolled to: what covers the
    // strip round a pinned row is sticky itself, and the browser moves it
    // with the scroll. (Covers switched on from here, on the scroll event,
    // came a frame after the row was pinned, and what was behind showed.)
    const measurePin = () => {
      const row = wrapperRef.current;
      if (!row) {
        return;
      }
      const style = getComputedStyle(list);
      const next = {
        top: Number.parseFloat(style.paddingTop) || 0,
        bottom: Number.parseFloat(style.paddingBottom) || 0,
        left: Number.parseFloat(style.paddingLeft) || 0,
        right: Number.parseFloat(style.paddingRight) || 0,
      };
      setPad((prev) =>
        prev.top === next.top &&
        prev.bottom === next.bottom &&
        prev.left === next.left &&
        prev.right === next.right
          ? prev
          : next,
      );

      const openHead = row.querySelector<HTMLElement>(
        "[data-selectable-row-head]",
      );
      const card = openHead?.parentElement;
      if (openHead && card) {
        // Where the head lies in the row, and what the row is besides it.
        // Read off the card, which moves with the row, not off the head,
        // which holds itself and so is not always where it lies.
        const lead =
          card.getBoundingClientRect().top -
          row.getBoundingClientRect().top +
          (Number.parseFloat(getComputedStyle(card).borderTopWidth) || 0) +
          (Number.parseFloat(getComputedStyle(openHead).marginTop) || 0);
        // Heights to the fraction: rows are rarely whole pixels, and a
        // rounded one leaves the head a hair off the line.
        const headHeight = openHead.getBoundingClientRect().height;
        const rowHeight = row.getBoundingClientRect().height;
        const cardEdge =
          Number.parseFloat(getComputedStyle(card).borderBottomWidth) || 0;
        const next = {
          // Held here the row's end - its card's inside - is the head's
          // height past the line: the head, holding itself on the line,
          // is at the end of what it may hold to, and is not pushed off.
          top: stickyActiveRowInsetTop - (rowHeight - headHeight - cardEdge),
          // Held here the head's foot is on the line; the rest goes under.
          bottom: stickyActiveRowInsetBottom - (rowHeight - lead - headHeight),
        };
        // Onto the row straight away as well: a row growing open changes
        // these every frame, and through a render they came a frame late -
        // a head pinned at the bottom jumped about as its row grew.
        row.style.top = `${next.top}px`;
        row.style.bottom = `${next.bottom}px`;
        setHeadPin((prev) =>
          prev && prev.top === next.top && prev.bottom === next.bottom
            ? prev
            : next,
        );
        return;
      }
      row.style.removeProperty("top");
      row.style.removeProperty("bottom");
      setHeadPin((prev) => (prev === null ? prev : null));
      const body = row.querySelector<HTMLElement>("[data-selectable-row-body]");
      const lead = body
        ? body.getBoundingClientRect().top - row.getBoundingClientRect().top
        : 0;
      setBodyLead((prev) => (prev === lead ? prev : lead));

      const rowFits =
        row.offsetHeight <=
        list.clientHeight -
          next.top -
          next.bottom -
          stickyActiveRowInsetTop -
          stickyActiveRowInsetBottom;
      setFits((prev) => (prev === rowFits ? prev : rowFits));
    };

    measurePinRef.current = measurePin;
    measurePin();
    const resizeObserver = new ResizeObserver(measurePin);
    resizeObserver.observe(list);
    resizeObserver.observe(row);

    return () => {
      resizeObserver.disconnect();
      // Only what the pin wrote goes: a row the list places itself (a
      // virtualized one, by its ``top``) keeps where it was put.
      row.style.removeProperty("top");
      row.style.removeProperty("bottom");
    };
  }, [stickyActiveRowInsetTop, stickyActiveRowInsetBottom, wantsSticky]);

  // Measured again after every render, before it is painted: a row opening
  // or shutting changes what is pinned in the same frame.
  useLayoutEffect(() => {
    measurePinRef.current?.();
  });

  // Whether an open row's end is coming up under its held head: only then is
  // the end drawn again over the head (``data-selectable-row-end``) - drawn
  // always, its ground squared off the card's own corners over their lift and
  // the keyboard's ring. Said a good way before the end gets there, so the
  // scroll event coming a frame late shows nothing.
  useLayoutEffect(() => {
    const row = wrapperRef.current;
    const list = row?.closest<HTMLElement>("[data-selectable-card-list]");
    if (!row || !list) return;
    const sayEndHeld = () => {
      const head = row.querySelector<HTMLElement>("[data-selectable-row-head]");
      const card = head?.parentElement;
      row.toggleAttribute(
        SELECTABLE_ROW_END_HELD_ATTR,
        head != null &&
          card != null &&
          card.getBoundingClientRect().bottom -
            head.getBoundingClientRect().bottom <
            HELD_ROW_END_LEAD +
              (Number.parseFloat(
                getComputedStyle(card).borderBottomLeftRadius,
              ) || 0),
      );
    };
    sayEndHeld();
    list.addEventListener("scroll", sayEndHeld, { passive: true });
    // And as the row opens and shuts, which no render follows to its end.
    const resizeObserver = new ResizeObserver(sayEndHeld);
    resizeObserver.observe(row);
    return () => {
      list.removeEventListener("scroll", sayEndHeld);
      resizeObserver.disconnect();
    };
  });

  // What covers the strip at the list's edge while the row is pinned there.
  // Each lies behind the row's own edge, reaching in under it, and sticks to
  // the list's very edge rather than the line the row is held on - so it only
  // comes out from behind the row once the row is held and everything that
  // was beyond the row has gone on past the edge. Rows reach the edge until
  // then; after, the ones sliding under the row do not show round it.
  const cover = (edge: "top" | "bottom") => {
    const inset = edge === "top" ? pad.top : pad.bottom;
    const height = inset + HELD_ROW_COVER_REACH + HELD_ROW_COVER_OVERHANG;
    return (
      <div
        aria-hidden
        data-selectable-row-cover={edge}
        // Over the rows that slide under the pinned one, under the row.
        className={cn(
          edge === "top"
            ? "sticky top-(--cover-edge) mb-(--cover-pull)"
            : "sticky bottom-(--cover-edge) mt-(--cover-pull)",
          "h-(--cover-height) ml-(--cover-left) mr-(--cover-right) z-1 shrink-0 bg-(--selectable-row-ground,transparent) pointer-events-none",
        )}
        style={
          {
            "--cover-edge": `${-(inset + HELD_ROW_COVER_OVERHANG)}px`,
            "--cover-height": `${height}px`,
            "--cover-pull": `${-height}px`,
            "--cover-left": `${-pad.left}px`,
            "--cover-right": `${-pad.right}px`,
          } as CSSProperties
        }
      />
    );
  };

  return (
    <>
      {useCssSticky ? cover("top") : null}
      <RowElement
        ref={setWrapperRef}
        data-selectable-row=""
        data-selectable-row-active={(animatesRemoval && isActive) || undefined}
        {...(animatesRemoval
          ? rowFoldsAway(isClosingUp, rowsKey, isFillingIn)
          : {})}
        {...(animatesRemoval && heldAcross.current.isHeld
          ? { layout: false }
          : {})}
        className={cn(
          scrollInsetTop > 0 && "scroll-mt-(--scroll-margin-top)",
          // Never squeezed as a flex item: in a list longer than its scroller,
          // a row on its way out would be flattened the moment it went.
          animatesRemoval && "shrink-0",
          // Over what covers the strip round it: its own line and lift are
          // drawn whole.
          useCssSticky &&
            "sticky top-(--sticky-top) bottom-(--sticky-bottom) z-3 shrink-0 self-stretch w-full overflow-visible",
        )}
        style={
          {
            ...wrapperStyle,
            ...(scrollInsetTop > 0
              ? { "--scroll-margin-top": `${scrollInsetTop}px` }
              : {}),
            ...(useCssSticky
              ? {
                  "--sticky-top": `${headPin ? headPin.top : stickyActiveRowInsetTop - bodyLead}px`,
                  "--sticky-bottom": `${headPin ? headPin.bottom : stickyActiveRowInsetBottom}px`,
                }
              : {}),
          } as CSSProperties
        }
      >
        {canSwipe ? (
          <div className="relative">
            {swipeOffset !== 0 ? (
              <div
                data-selectable-row-swipe-actions=""
                // Only the room the row has left: a row with no ground of
                // its own would show them through it.
                className={cn(
                  "absolute top-0 bottom-0 right-1 w-(--swipe-width) flex justify-end overflow-hidden",
                  rowRadiusClassName,
                )}
                style={
                  {
                    // A width of one or less is a share of the row's, as Joy
                    // read a number.
                    "--swipe-width":
                      -swipeOffset > 0 && -swipeOffset <= 1
                        ? `${-swipeOffset * 100}%`
                        : `${-swipeOffset}px`,
                  } as CSSProperties
                }
              >
                {/* Panels the row's height, as a phone draws a row's swipe
                    actions - not controls at a size - in a variant's colours. */}
                {swipeActions!.map((action) => (
                  <button
                    type="button"
                    key={action.key}
                    data-testid={`swipe-action-${action.key}`}
                    onClick={() => {
                      setSwipeOffset(0);
                      action.onAction();
                    }}
                    className={cn(
                      buttonVariants({ variant: action.variant }),
                      "w-20 shrink-0 h-full rounded-none flex-col gap-1",
                    )}
                  >
                    {action.icon}
                    {action.label}
                  </button>
                ))}
              </div>
            ) : null}
            <div
              {...swipeHandlers}
              className={cn(
                "relative [touch-action:pan-y] transform-(--transform-2)",
                isSwiping
                  ? "transition-normal [transition-duration:0s] [transition-timing-function:ease] delay-[0s] transition-none"
                  : "transition-normal [transition-duration:200ms] [transition-timing-function:ease-out] delay-[0s] [transition-property:transform]",
                "motion-reduce:transition-normal [@media(prefers-reduced-motion:_reduce)]:[transition-duration:0s] [@media(prefers-reduced-motion:_reduce)]:[transition-timing-function:ease] motion-reduce:delay-[0s] motion-reduce:transition-none",
              )}
              style={
                {
                  "--transform-2": `translateX(${swipeOffset}px)`,
                } as CSSProperties
              }
            >
              {children}
            </div>
          </div>
        ) : (
          children
        )}
      </RowElement>
      {useCssSticky ? cover("bottom") : null}
    </>
  );
}

function useSelectableCardListScrollRef<T extends HTMLElement>() {
  const scrollRootRef = useRef<T | null>(null);

  // Do not attach OverlayScrollbars to this element. OS reparents children under
  // an internal viewport; React then fails with insertBefore NotFoundError when
  // list rows/groups update (e.g. triage selection rebuilds suggestion options).
  const setScrollRef = useCallback((node: T | null) => {
    scrollRootRef.current = node;
  }, []);

  return { scrollRootRef, setScrollRef };
}

/*
 * A sticky header fades into the rows scrolling under it (its ``::after``)
 * instead of ending on a hairline. Drawn below the header, so it takes no room
 * in the list.
 */

function SelectableCardListGroupLabel({
  label,
  labelContent,
  labelEnd,
  subtitle,
  stickyGroupLabels,
  stickyGroupLabelHeight,
  stickySectionLabelHeight,
  flush,
  hasSections,
}: {
  label: string;
  labelContent?: ReactNode;
  labelEnd?: ReactNode;
  subtitle?: string;
  stickyGroupLabels: boolean;
  stickyGroupLabelHeight: number;
  stickySectionLabelHeight: number;
  flush: boolean;
  /** A sticky section label sits right under this one and fades instead. */
  hasSections: boolean;
}) {
  const hasRichHeader =
    labelContent != null || subtitle != null || labelEnd != null;

  if (stickyGroupLabels && hasRichHeader) {
    return (
      <div
        className={cn(
          "sticky top-0 z-3 flex items-center justify-between gap-2",
          subtitle
            ? "min-h-(--sticky-section-label-height)"
            : "min-h-(--sticky-group-label-height)",
          "shrink-0 pl-4 pr-4",
          subtitle ? "pt-1.5 pb-1.5" : "pt-0 pb-0",
          "bg-background",
          hasSections
            ? ""
            : "[&::after]:[content:''] [&::after]:absolute [&::after]:top-full [&::after]:left-0 [&::after]:right-0 [&::after]:h-4 [&::after]:pointer-events-none [&::after]:bg-[linear-gradient(var(--background),transparent)] [&::after]:[background-position-x:initial] [&::after]:[background-position-y:initial] [&::after]:bg-size-[initial] [&::after]:[background-repeat:initial] [&::after]:[background-attachment:initial] [&::after]:[background-origin:initial] [&::after]:[background-clip:initial] [&::after]:bg-[initial]",
        )}
        style={
          {
            "--sticky-section-label-height": subtitle
              ? stickySectionLabelHeight <= 1 && stickySectionLabelHeight !== 0
                ? `${stickySectionLabelHeight * 100}%`
                : `${stickySectionLabelHeight}px`
              : undefined,
            "--sticky-group-label-height": !subtitle
              ? stickyGroupLabelHeight <= 1 && stickyGroupLabelHeight !== 0
                ? `${stickyGroupLabelHeight * 100}%`
                : `${stickyGroupLabelHeight}px`
              : undefined,
          } as CSSProperties
        }
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {labelContent ?? (
            <Typography level="body-sm" className="font-medium text-foreground">
              {label}
            </Typography>
          )}
          {subtitle ? (
            <Typography
              level="body-xs"
              className="text-muted-foreground mt-0.5 line-clamp-2"
            >
              {subtitle}
            </Typography>
          ) : null}
        </div>
        {labelEnd ? (
          <div
            className="shrink-0 flex items-center"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            {labelEnd}
          </div>
        ) : null}
      </div>
    );
  }

  if (stickyGroupLabels) {
    return (
      <div
        className={cn(
          "sticky top-0 z-3 h-(--sticky-group-label-height) flex items-center shrink-0 pl-4 pr-4 bg-background",
          hasSections
            ? ""
            : "[&::after]:[content:''] [&::after]:absolute [&::after]:top-full [&::after]:left-0 [&::after]:right-0 [&::after]:h-4 [&::after]:pointer-events-none [&::after]:bg-[linear-gradient(var(--background),transparent)] [&::after]:[background-position-x:initial] [&::after]:[background-position-y:initial] [&::after]:bg-size-[initial] [&::after]:[background-repeat:initial] [&::after]:[background-attachment:initial] [&::after]:[background-origin:initial] [&::after]:[background-clip:initial] [&::after]:bg-[initial]",
        )}
        style={
          {
            "--sticky-group-label-height":
              stickyGroupLabelHeight <= 1 && stickyGroupLabelHeight !== 0
                ? `${stickyGroupLabelHeight * 100}%`
                : `${stickyGroupLabelHeight}px`,
          } as CSSProperties
        }
      >
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 uppercase [letter-spacing:0.04em]"
        >
          {label}
        </Typography>
      </div>
    );
  }

  return (
    <Typography
      level="body-xs"
      className={cn(
        flush ? "pl-4 pr-4" : "pl-1.5 pr-1.5",
        "pt-2.5 pb-1 text-muted-foreground/80 uppercase [letter-spacing:0.04em]",
      )}
    >
      {label}
    </Typography>
  );
}

function SelectableCardListSectionLabel({
  label,
  labelContent,
  subtitle,
  stickyGroupLabels,
  stickyGroupLabelHeight,
  stickySectionLabelHeight,
}: {
  label: string;
  labelContent?: ReactNode;
  subtitle?: string;
  stickyGroupLabels: boolean;
  stickyGroupLabelHeight: number;
  stickySectionLabelHeight: number;
}) {
  if (stickyGroupLabels) {
    return (
      <div
        className={cn(
          "sticky top-(--sticky-group-label-height) z-2 flex flex-col justify-center",
          subtitle ? "gap-0.5" : "gap-0",
          "min-h-(--sticky-section-label-height) shrink-0 pl-4 pr-4 pt-1.5 pb-1.5 bg-background [&::after]:[content:''] [&::after]:absolute [&::after]:top-full [&::after]:left-0 [&::after]:right-0 [&::after]:h-4 [&::after]:pointer-events-none [&::after]:bg-[linear-gradient(var(--background),transparent)] [&::after]:[background-position-x:initial] [&::after]:[background-position-y:initial] [&::after]:bg-size-[initial] [&::after]:[background-repeat:initial] [&::after]:[background-attachment:initial] [&::after]:[background-origin:initial] [&::after]:[background-clip:initial] [&::after]:bg-[initial]",
        )}
        style={
          {
            "--sticky-group-label-height": `${stickyGroupLabelHeight}px`,
            "--sticky-section-label-height":
              stickySectionLabelHeight <= 1 && stickySectionLabelHeight !== 0
                ? `${stickySectionLabelHeight * 100}%`
                : `${stickySectionLabelHeight}px`,
          } as CSSProperties
        }
      >
        {labelContent ?? (
          <Typography level="body-sm" className="font-medium text-foreground">
            {label}
          </Typography>
        )}
        {subtitle ? (
          <Typography
            level="body-xs"
            className="text-muted-foreground line-clamp-2"
          >
            {subtitle}
          </Typography>
        ) : null}
      </div>
    );
  }

  return (
    <div className="pl-4 pr-4 pt-2 pb-1">
      {labelContent ?? (
        <Typography level="body-sm" className="font-medium text-foreground">
          {label}
        </Typography>
      )}
      {subtitle ? (
        <Typography level="body-xs" className="text-muted-foreground mt-0.5">
          {subtitle}
        </Typography>
      ) : null}
    </div>
  );
}

function SelectableCardListRow<T>({
  item,
  index,
  isActive,
  isFocusWithin,
  group,
  estimatedItemHeight,
  variableItemHeight,
  renderItem,
  rowClassName,
  getItemSwipeActions,
  activeRowClassName,
  hasDivider,
  dividerVisible,
  flush,
  scrollInsetTop = 0,
  stickyActiveRow = false,
  stickyActiveRowInsetTop = 0,
  stickyActiveRowInsetBottom = 0,
  registerRowRef,
  onRowActivate,
  getRowClassName,
  getItemKey,
  onRowHover,
  loading,
}: {
  item: T;
  index: number;
  isActive: boolean;
  isFocusWithin: boolean;
  group?: SelectableCardListGroup<T>;
  estimatedItemHeight: number;
  variableItemHeight: boolean;
  renderItem: SelectableCardListProps<T>["renderItem"];
  rowClassName?: string;
  getItemSwipeActions?: SelectableCardListProps<T>["getItemSwipeActions"];
  activeRowClassName?: string;
  hasDivider: boolean;
  dividerVisible: boolean;
  flush: boolean;
  scrollInsetTop?: number;
  stickyActiveRow?: boolean;
  stickyActiveRowInsetTop?: number;
  stickyActiveRowInsetBottom?: number;
  onRowActivate: (item: T, index: number, clickCount?: number) => void;
  onRowHover: (item: T, index: number) => void;
  getRowClassName: SelectableListContext<T>["getRowClassName"];
  registerRowRef: (index: number, element: HTMLElement | null) => void;
  getItemKey: (item: T, index: number) => string;
  /** The row is the list's placeholder: it is still loading. */
  loading: boolean;
}) {
  const { rowInsetClassName } = useSelectableCardRowStyle();

  return (
    <SelectableCardListRowShell
      key={getItemKey(item, index)}
      index={index}
      isActive={isActive}
      stickyActiveRow={stickyActiveRow}
      stickyActiveRowInsetTop={stickyActiveRowInsetTop}
      stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
      registerRowRef={registerRowRef}
      scrollInsetTop={scrollInsetTop}
      swipeActions={loading ? null : (getItemSwipeActions?.(item) ?? null)}
    >
      {hasDivider ? (
        <div
          data-selectable-row-divider=""
          className={cn(
            "h-px",
            dividerVisible ? "bg-border" : "bg-transparent",
            flush ? "ml-0 mr-0" : "ml-3 mr-3",
            "[transition:background-color_120ms_ease]",
          )}
        />
      ) : null}
      <div
        data-selectable-row-body=""
        inert={loading || undefined}
        onClickCapture={(event) =>
          handleSelectableCardRowClickCapture(event, (clickCount) =>
            onRowActivate(item, index, clickCount),
          )
        }
        onMouseEnter={() => onRowHover(item, index)}
        className={cn(
          "flex flex-col justify-start gap-1",
          rowInsetClassName,
          flush ? "ml-0 mr-0" : "ml-1 mr-1",
          !variableItemHeight && "min-h-(--min-height)",
          getRowClassName({
            isActive,
            className: cn(rowClassName, isActive && activeRowClassName),
          }),
        )}
        style={
          variableItemHeight
            ? undefined
            : ({ "--min-height": `${estimatedItemHeight}px` } as CSSProperties)
        }
      >
        {renderItem({
          item,
          index,
          isActive,
          isFocusWithin,
          group,
          isLoading: loading,
        })}
      </div>
    </SelectableCardListRowShell>
  );
}

function SelectableCardListDirectBody<T>({
  context,
  estimatedItemHeight,
  variableItemHeight,
  showRowDividers,
  rowDividerPlacement,
  flush,
  rowHover,
  activeRowDividers,
  loading,
  loadMore,
  loadingMoreRows,
  renderItem,
  renderItemDetails,
  isItemSelected,
  rowClassName,
  getItemSwipeActions,
  activeRowClassName,
  groupItemsClassName,
  stickyActiveRow = false,
  animatesRemovals = false,
  isFillingIn = false,
  listKey,
  stickyActiveRowInsetTop = 0,
  stickyActiveRowInsetBottom = 0,
  className,
}: {
  context: SelectableListContext<T>;
  estimatedItemHeight: number;
  variableItemHeight: boolean;
  showRowDividers: boolean;
  rowDividerPlacement: "between" | "overline";
  flush: boolean;
  rowHover: SelectableRowHover;
  activeRowDividers: "hide" | "keep";
  /** The rows are the list's placeholder: it is still loading. */
  loading: boolean;
  loadMore?: SelectableCardListLoadMore;
  /**
   * Loading more, the rows still to come: the placeholder, drawn after the
   * rows in hand as the rows are while the list loads - inert.
   */
  loadingMoreRows: T[];
  renderItem: SelectableCardListProps<T>["renderItem"];
  renderItemDetails?: SelectableCardListProps<T>["renderItemDetails"];
  isItemSelected?: SelectableCardListProps<T>["isItemSelected"];
  rowClassName?: string;
  getItemSwipeActions?: SelectableCardListProps<T>["getItemSwipeActions"];
  activeRowClassName?: string;
  groupItemsClassName?: string;
  stickyActiveRow?: boolean;
  animatesRemovals?: boolean;
  isFillingIn?: boolean;
  listKey?: string;
  stickyActiveRowInsetTop?: number;
  stickyActiveRowInsetBottom?: number;
  className?: string;
}) {
  const { size, rowInsetClassName, rowRadius } = useSelectableCardRowStyle();
  const { variant } = useSelectableListStyle();
  // Which rows are open, when the list's rows are accordions - and which are
  // still shrinking shut, which keep what they opened to until it has gone.
  const [openKeys, setOpenKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [shuttingKeys, setShuttingKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const toggleOpenKey = useCallback(
    (key: string) => {
      const wasOpen = openKeys.has(key);
      setOpenKeys((current) => {
        const next = new Set(current);
        if (wasOpen) next.delete(key);
        else next.add(key);
        return next;
      });
      setShuttingKeys((current) => {
        if (wasOpen === current.has(key)) return current;
        const next = new Set(current);
        if (wasOpen) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    [openKeys],
  );
  const finishShutting = useCallback((key: string) => {
    setShuttingKeys((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);
  // The card's line is its border; an open head covers it to draw the card's
  // top itself (below).
  const cardEdge = variant === "outlined" && !flush ? 1 : 0;
  // A list that keeps rows in sight covers the strip round the ones it holds
  // in what it stands on (see the shell, and an open row's head below).
  const holdsRows = stickyActiveRow || renderItemDetails != null;
  // Between the list's inside and a card's edge: the list's inset, then the
  // card's own margin. Said on the list, for what an open row draws past its
  // card (``--selectable-list-inset``, ``--selectable-card-margin``).
  const listInset = flush ? "0px" : `${SELECTABLE_LIST_INSET[size] * 8}px`;
  const cardMargin = flush ? "0px" : "4px";
  // What the list stands on - the first surface behind its rows, which are
  // clear, as the list itself usually is - for those bands to be. Read again
  // as the scheme changes: the page's colours change under the list without
  // drawing it again, and a ground read in the last scheme was a band of it
  // round the held row.
  useColorScheme();
  const [ground, setGround] = useState("transparent");
  useLayoutEffect(() => {
    if (!holdsRows) return;
    let found = "transparent";
    for (
      let element: HTMLElement | null = scrollRootRef.current;
      element;
      element = element.parentElement
    ) {
      const color = getComputedStyle(element).backgroundColor;
      if (color && color !== "transparent" && color !== "rgba(0, 0, 0, 0)") {
        found = color;
        break;
      }
    }
    setGround((prev) => (prev === found ? prev : found));
  });
  const { scrollRootRef, setScrollRef } =
    useSelectableCardListScrollRef<HTMLDivElement>();
  const {
    items,
    getItemKey,
    isItemActive,
    onRowActivate,
    getRowClassName,
    registerRowRef,
    onRowHover,
    isFocusWithin,
  } = context;
  // A row opens as it is picked and shuts as it is let go - by a click, Enter,
  // a checkbox, anything - so it is the picking that is watched, not whatever
  // did it. A row's own control changes only whether it is open, so between
  // pickings it stays as the control left it.
  const selectedKeys =
    renderItemDetails && isItemSelected && !loading
      ? items.flatMap((item, index) =>
          isItemSelected(item, index) ? [String(getItemKey(item, index))] : [],
        )
      : [];
  const selectedKeysSaid = selectedKeys.join("\u0000");
  const wasSelectedRef = useRef<ReadonlySet<string> | null>(null);
  useLayoutEffect(() => {
    const now = new Set(
      selectedKeysSaid ? selectedKeysSaid.split("\u0000") : [],
    );
    const was = wasSelectedRef.current ?? new Set<string>();
    wasSelectedRef.current = now;
    const picked = [...now].filter((key) => !was.has(key));
    const letGo = [...was].filter((key) => !now.has(key));
    if (!picked.length && !letGo.length) return;
    setOpenKeys((current) => {
      const next = new Set(current);
      for (const key of picked) next.add(key);
      for (const key of letGo) next.delete(key);
      return next;
    });
    setShuttingKeys((current) => {
      const next = new Set(current);
      for (const key of picked) next.delete(key);
      for (const key of letGo) next.add(key);
      return next;
    });
  }, [selectedKeysSaid]);
  // Where the chosen row is, when the list keeps it in sight: the rows after
  // it go up under it once it is pinned at the top - and by the time one of
  // theirs would reach the line, it is. So none of them holds its head.
  const pinnedIndex = stickyActiveRow
    ? items.findIndex((item, index) => isItemActive?.(item, index) ?? false)
    : -1;

  const rows = loadingMoreRows.length ? [...items, ...loadingMoreRows] : items;
  // Rows taken out since the list was last drawn: those left glide into their
  // place once they have slid away; rows put in, at once.
  const drawnKeys = useRef<ReadonlySet<string>>(new Set());
  const rowKeys = new Set(
    loading ? [] : items.map((item, index) => String(getItemKey(item, index))),
  );
  const isClosingUp = [...drawnKeys.current].some((key) => !rowKeys.has(key));
  const drawnRowsKey = [...rowKeys].join(" ");
  // What the rows glide on (``layoutDependency``): the rows the list has -
  // but not as rows only fill in round those drawn, where the list is
  // scrolled by as far as they move the rest and nothing on screen moves. A
  // glide there would be from where each was in the list, not on screen.
  const glidesOn = useRef(drawnRowsKey);
  if (!isFillingIn) glidesOn.current = drawnRowsKey;
  // And where rows were drawn standing in for a page being read: the page's
  // rows come in in their place as they are, not slid in as rows newly put
  // in are - they are what stood there, read.
  const drawnStanding = useRef({ from: 0, to: 0 });
  useEffect(() => {
    drawnKeys.current = rowKeys;
    drawnStanding.current = {
      from: items.length,
      to: items.length + loadingMoreRows.length,
    };
  });
  // The row the keys are on kept where it is on screen as rows are taken out
  // round it or put back: the list scrolled by as far as they move it, as far
  // as it scrolls - before its rows' glides are measured, so it does not
  // glide but where the list scrolls no further, the rest gliding to and from
  // it. Where it stands unheld, as one held at the top or the foot stands
  // anywhere; a row on its way out, laid over the rest, is not in it.
  const measureActiveRow = () => {
    const row = scrollRootRef.current?.querySelector<HTMLElement>(
      "[data-selectable-row-active]:not([data-motion-pop-id])",
    );
    if (!row) return null;
    const position = row.style.position;
    row.style.position = "static";
    const top = row.offsetTop;
    row.style.position = position;
    return top;
  };
  const activeTopRef = useRef<number | null>(null);
  const measuredRowsKey = useRef(drawnRowsKey);
  if (animatesRemovals && measuredRowsKey.current !== drawnRowsKey) {
    measuredRowsKey.current = drawnRowsKey;
    const measured = measureActiveRow();
    activeTopRef.current =
      measured == null
        ? null
        : measured - (scrollRootRef.current?.scrollTop ?? 0);
  }
  useLayoutEffect(() => {
    const top = activeTopRef.current;
    activeTopRef.current = null;
    const measured = top == null ? null : measureActiveRow();
    if (top == null || measured == null || !scrollRootRef.current) return;
    scrollRootRef.current.scrollTop = measured - top;
    // Measured on each new set of rows alone.
  }, [drawnRowsKey]);

  const rowsContent = rows.map((item, index) => {
    const isPlaceholder = loading || index >= items.length;
    const isActive = !isPlaceholder && (isItemActive?.(item, index) ?? false);
    const prevItem = index > 0 ? items[index - 1] : undefined;
    const isPrevActive = prevItem
      ? (isItemActive?.(prevItem, index - 1) ?? false)
      : false;
    const hasDivider =
      showRowDividers && (rowDividerPlacement === "overline" || index > 0);
    const dividerVisible =
      activeRowDividers === "keep" || (!isActive && !isPrevActive);
    const key = isPlaceholder
      ? `__loading-${index}`
      : String(getItemKey(item, index));
    // A placeholder can open as the row it stands for can - it has the
    // control - though, inert, it never is.
    const details = renderItemDetails?.({ item, index, isActive });
    const canOpen = details != null && details !== false;
    const isOpen = canOpen && openKeys.has(key);
    // Open, or still shrinking shut: the row keeps its open look - its
    // held head and what the list draws round it - until it has gone.
    const isShown = canOpen && (isOpen || shuttingKeys.has(key));
    const holdsHead = isShown && !(pinnedIndex >= 0 && index > pinnedIndex);
    const args = {
      item,
      index,
      isActive,
      isFocusWithin,
      isLoading: isPlaceholder,
      ...(renderItemDetails
        ? { isOpen, canOpen, toggleOpen: () => toggleOpenKey(key) }
        : {}),
    };

    const card = (
      <div
        data-selectable-row-body=""
        inert={isPlaceholder || undefined}
        onClickCapture={
          isPlaceholder
            ? undefined
            : (event) =>
                handleSelectableCardRowClickCapture(event, (clickCount) =>
                  onRowActivate(item, index, clickCount),
                )
        }
        onMouseEnter={isPlaceholder ? undefined : () => onRowHover(item, index)}
        className={cn(
          "flex flex-col justify-start",
          renderItemDetails ? "gap-0" : "gap-1",
          // Whatever a row stacks inside itself stays inside it: Joy,
          // for one, lifts a chip's label over the chip's own click
          // target. Loose in the list, that label stood level with what
          // covers the strip round a held row, and - later in the page
          // than the cover above a row pinned at the top - showed over it.
          holdsRows && "isolate",
          // An accordion's head and what it opens to take the inset
          // themselves, so the head can reach the card's own edges.
          !renderItemDetails && rowInsetClassName,
          flush ? "ml-0 mr-0" : "ml-1 mr-1",
          !variableItemHeight && "min-h-(--min-height)",
          getRowClassName({
            isActive,
            className: cn(rowClassName, isActive && activeRowClassName),
            // Open, the card takes no surface under the pointer.
            hasHoverSurface: !isShown,
          }),
          // Open, the card's top is drawn by its head (below), held or
          // not. Its own top line is left out, and its surface kept
          // inside its line: scrolling back to the row, the real top
          // comes down through the strip above the held head, and its
          // line hung there on its own - and even under what covers the
          // strip, the row of pixels at its very top, line or surface,
          // showed a sliver where the two are rounded to the pixel apart.
          // Shut again, the card takes its own top back from the head in
          // one frame, so that line is not faded back in: easing in from
          // nothing, over a surface that already reached under it, it
          // showed the card's top as a strip of its surface as it shut.
          renderItemDetails &&
            "[transition:background-color_120ms_ease,box-shadow_120ms_ease,border-left-color_120ms_ease,border-right-color_120ms_ease,border-bottom-color_120ms_ease]",
          isShown &&
            "border-t-transparent bg-clip-padding [&:hover:not([data-selectable-row-hover='title']_*)]:border-t-transparent",
        )}
        style={
          variableItemHeight
            ? undefined
            : ({
                "--min-height": `${estimatedItemHeight}px`,
              } as CSSProperties)
        }
      >
        {renderItemDetails ? (
          <>
            {holdsHead ? (
              // What covers the strip above the head once it is held:
              // lying behind the head's top at rest, and stuck to the
              // list's very edge - so it comes out from behind the head
              // only once the head is held and the row before this one
              // has gone on past the edge. From the card's own edge, not
              // inside it: scrolling back to the row, its real top comes
              // down through the strip to the held head, and its line -
              // left out - hung there on its own above the head. Over
              // what the row opened to, under the head.
              <div
                aria-hidden
                data-selectable-row-cover="head"
                className="sticky top-[calc(-1*(var(--selectable-list-inset)+2px))] h-[calc(var(--selectable-list-inset)+14px)] mt-[calc(-1*var(--selectable-card-edge))] mb-[calc(-1*(var(--selectable-list-inset)+14px-var(--selectable-card-edge)))] ml-[calc(-1*(var(--selectable-list-inset)+var(--selectable-card-margin)+var(--selectable-card-edge)))] mr-[calc(-1*(var(--selectable-list-inset)+var(--selectable-card-margin)+var(--selectable-card-edge)))] z-1 shrink-0 bg-(--selectable-row-ground,transparent) pointer-events-none"
              />
            ) : null}
            {/* The head. Open, it is held in sight while what the row
                      opened to scrolls under it (the shell holds it), and it
                      draws the card's own top: the card's line, corners and
                      surface, on a square of the list's ground spread over
                      the card's edges. Held inside the card's border instead,
                      the border beside it was cut by the strip covered above
                      it, and the card's straight sides - and what scrolled
                      under the head - showed round its rounded corners. */}
            <div
              data-selectable-row-head={holdsHead ? "" : undefined}
              className={cn(
                isShown &&
                  // The keyboard's ring round the card's top and down
                  // its sides beside the head (``::before``, outside the
                  // card's line, which this box's edge is - drawn round,
                  // not over, it): this ground covers the card's own
                  // there, and the head itself is clipped to cast no
                  // shadow above it.
                  "ml-[calc(-1*var(--selectable-card-edge))] mr-[calc(-1*var(--selectable-card-edge))] mt-[calc(-1*var(--selectable-card-edge))] bg-(--selectable-row-ground,transparent) relative [&::before]:[content:''] [&::before]:absolute [&::before]:-top-0.5 [&::before]:-left-0.5 [&::before]:-right-0.5 [&::before]:bottom-0 [&::before]:rounded-tl-[calc(var(--selectable-row-radius)+2px)] [&::before]:rounded-tr-[calc(var(--selectable-row-radius)+2px)] [&::before]:z-1 [&::before]:pointer-events-none",
                isShown &&
                  (isActive && isFocusWithin
                    ? "[&::before]:[border-width:2px] [&::before]:[border-style:solid] [&::before]:border-[color-mix(in_srgb,var(--muted-foreground)_14%,var(--selectable-row-ground,transparent))] [&::before]:[border-bottom-width:initial] [&::before]:[border-bottom-style:none] [&::before]:border-b-[initial]"
                    : "[&::before]:[border-width:2px] [&::before]:[border-style:solid] [&::before]:border-transparent [&::before]:[border-bottom-width:initial] [&::before]:[border-bottom-style:none] [&::before]:border-b-[initial]"),
                isShown &&
                  holdsHead &&
                  // Held where a pinned row stops, while what the row
                  // opened to scrolls up under it; the cover before it
                  // covers the strip above. Beside the head, from the
                  // list's one edge to the other (``::after``): what the
                  // card underneath casts up its sides would otherwise
                  // show there, straight past the head's rounded
                  // corners. Behind the head, which casts its own.
                  "sticky top-(--selectable-held-head-top) z-2 [&::after]:[content:''] [&::after]:absolute [&::after]:top-0 [&::after]:bottom-0 [&::after]:left-[calc(-1*(var(--selectable-list-inset)+var(--selectable-card-margin)))] [&::after]:right-[calc(-1*(var(--selectable-list-inset)+var(--selectable-card-margin)))] [&::after]:bg-(--selectable-row-ground,transparent) [&::after]:z-[-1] [&::after]:pointer-events-none",
              )}
            >
              <div
                className={
                  isShown
                    ? cn(
                        getRowClassName({ isActive }),
                        rowInsetClassName,
                        // The keyboard's ring is the box round it's to
                        // draw, past this one's clip. Its line is put on
                        // as the row opens, so it is not faded in: a
                        // border that eases in starts from the text's
                        // colour, and the card's top flashed in it as the
                        // row opened. The card's shadow too, but only
                        // down its sides and round its corners: none
                        // above the line it is held on, and none cast
                        // down onto what it opened to. (Cut to nothing,
                        // the card underneath went on casting its own
                        // straight up beside the rounded corners, squared
                        // off at the strip.)
                        "[outline:none] [transition:background-color_120ms_ease,box-shadow_120ms_ease] [clip-path:inset(0_-16px_0_-16px)]",
                        // A chosen card has a surface of its own; any
                        // other stands on the ground, which the head
                        // still has to be opaque in.
                        !isActive && "bg-(--selectable-row-ground,transparent)",
                        // Square under it, where what it opened to goes
                        // on, and no line between - the card's own edge
                        // stops at its sides: what it opened to is a
                        // card of its own inside this one.
                        "[border-bottom-width:0] rounded-bl-none rounded-br-none",
                      )
                    : rowInsetClassName
                }
              >
                {renderItem(args)}
              </div>
            </div>
            {isShown ? (
              <SelectableCardListRowDetails
                expanded={isOpen}
                onShut={() => finishShutting(key)}
                className={cn(rowInsetClassName, "pt-0 mt-1")}
              >
                {details}
              </SelectableCardListRowDetails>
            ) : null}
            {isShown && !flush ? (
              // The card's rounded end, drawn again over its head. Where
              // the end is further down it is the end the card draws
              // itself; once it has come up under the held head and
              // pushes it (``data-selectable-row-end-held``), it
              // swallows the head's square foot into the card's rounded
              // corners - as a thread card's header draws the rounded
              // top of a record stuck under it. In two layers, one
              // corner tall, on the card's own edges: the ground outside
              // each curve (a rounded box would clip its fill to the
              // curve and leave exactly those corners), then the card's
              // line round them, in its own colour.
              <div
                aria-hidden
                data-selectable-row-end=""
                className="hidden absolute left-[calc(-1*var(--selectable-card-edge))] right-[calc(-1*var(--selectable-card-edge))] bottom-[calc(-1*var(--selectable-card-edge))] h-(--selectable-row-radius) z-3 pointer-events-none border-inherit [[data-selectable-row-end-held]_&]:[display:block] [&::before]:[content:''] [&::before]:absolute [&::before]:inset-0 [&::before]:box-border [&::before]:pointer-events-none [&::after]:[content:''] [&::after]:absolute [&::after]:inset-0 [&::after]:box-border [&::after]:pointer-events-none [&::before]:bg-[radial-gradient(circle_at_100%_0,transparent_calc(var(--selectable-row-radius)-0.5px),var(--selectable-row-ground,transparent)_var(--selectable-row-radius)),radial-gradient(circle_at_0_0,transparent_calc(var(--selectable-row-radius)-0.5px),var(--selectable-row-ground,transparent)_var(--selectable-row-radius))] [&::before]:bg-position-[bottom_left,bottom_right] [&::before]:bg-size-[var(--selectable-row-radius)_var(--selectable-row-radius)] [&::before]:bg-no-repeat [&::after]:[border-style:solid] [&::after]:[border-width:0_var(--selectable-card-edge)_var(--selectable-card-edge)] [&::after]:border-inherit [&::after]:rounded-bl-(--selectable-row-radius) [&::after]:rounded-br-(--selectable-row-radius)"
              />
            ) : null}
          </>
        ) : (
          renderItem(args)
        )}
      </div>
    );
    return (
      <SelectableCardListRowShell
        key={key}
        index={index}
        // A placeholder giving way to the rows it stood for goes at once.
        animatesRemoval={animatesRemovals && !isPlaceholder}
        isClosingUp={isClosingUp}
        rowsKey={glidesOn.current}
        isFillingIn={
          isFillingIn ||
          (index >= drawnStanding.current.from &&
            index < drawnStanding.current.to)
        }
        isActive={isActive}
        stickyActiveRow={stickyActiveRow}
        stickyActiveRowInsetTop={stickyActiveRowInsetTop}
        stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
        registerRowRef={registerRowRef}
        swipeActions={
          isPlaceholder ? null : (getItemSwipeActions?.(item) ?? null)
        }
      >
        {hasDivider ? (
          <div
            data-selectable-row-divider=""
            className={cn(
              "h-px",
              dividerVisible ? "bg-border" : "bg-transparent",
              flush ? "ml-0 mr-0" : "ml-3 mr-3",
              "[transition:background-color_120ms_ease]",
            )}
          />
        ) : null}
        {card}
      </SelectableCardListRowShell>
    );
  });
  // Taken out, a row folds away - each kept till it has - but only out of the
  // list it was in: the rows of another list - another search, other
  // filters, or the placeholders - start afresh, and what they leave out goes
  // at once. Rows are of the key they were first drawn under: a list read
  // again under a new key can draw its old rows a while yet, so the first
  // rows to change after it changes are the other list's.
  const listed = useRef({ key: listKey, items });
  const rowsKey = items === listed.current.items ? listed.current.key : listKey;
  useEffect(() => {
    if (listed.current.items !== items)
      listed.current = { key: listKey, items };
  });
  const listContent = animatesRemovals ? (
    <AnimatePresence
      key={loading ? "placeholders" : `rows:${rowsKey ?? ""}`}
      initial={false}
      // A row taken out leaves the flow at once, the rest gliding into its
      // place as framer measures them.
      mode="popLayout"
    >
      {rowsContent}
    </AnimatePresence>
  ) : (
    rowsContent
  );

  // Its rows' glides measured as it is scrolled (``layoutScroll``) - and a
  // row taken out placed over it as it goes, by its own top.
  const ScrollElement = animatesRemovals ? motion.div : "div";
  return (
    <ScrollElement
      ref={setScrollRef}
      data-selectable-card-list=""
      data-selectable-row-hover={rowHover}
      {...(animatesRemovals ? { layoutScroll: true } : {})}
      className={cn(
        "flex-1 min-h-0 overflow-auto w-full",
        animatesRemovals && "relative",
        "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
        !flush && SELECTABLE_LIST_INSET_CLASS[size],
        // The hairlines beside a hovered row step aside for the card it
        // becomes; a flush row becomes no card, and a list whose rows keep
        // still under the pointer has nothing to step aside for.
        rowHover !== "title" &&
          !flush &&
          SELECTABLE_CARD_ROW_DIVIDER_HOVER_CLASS,
        className,
      )}
      style={
        holdsRows
          ? ({
              [SELECTABLE_ROW_GROUND_VAR]: ground,
              "--selectable-list-inset": listInset,
              "--selectable-card-margin": cardMargin,
              "--selectable-card-edge": `${cardEdge}px`,
              "--selectable-row-radius": rowRadius,
              "--selectable-held-head-top": `${stickyActiveRowInsetTop}px`,
            } as CSSProperties)
          : undefined
      }
    >
      {groupItemsClassName ? (
        <div className={cn(GROUP_ITEMS_CONTAINER_CLASS, groupItemsClassName)}>
          {listContent}
        </div>
      ) : (
        listContent
      )}
      {loadMore ? (
        <SelectableCardListLoadMoreSentinel
          loadMore={loadMore}
          scrollRootRef={scrollRootRef}
          flush={flush}
        />
      ) : null}
    </ScrollElement>
  );
}

function SelectableCardListBody<T>({
  context,
  estimatedItemHeight,
  activeItemHeight,
  variableItemHeight,
  showRowDividers,
  rowDividerPlacement,
  flush,
  rowHover,
  activeRowDividers,
  loading,
  loadMore,
  loadingMoreRows,
  renderItem,
  rowClassName,
  getItemSwipeActions,
  activeRowClassName,
  stickyActiveRow = false,
  stickyActiveRowInsetTop = 0,
  stickyActiveRowInsetBottom = 0,
  className,
}: {
  context: SelectableListContext<T>;
  estimatedItemHeight: number;
  activeItemHeight: number;
  variableItemHeight: boolean;
  showRowDividers: boolean;
  rowDividerPlacement: "between" | "overline";
  flush: boolean;
  rowHover: SelectableRowHover;
  activeRowDividers: "hide" | "keep";
  /** The rows are the list's placeholder: it is still loading. */
  loading: boolean;
  loadMore?: SelectableCardListLoadMore;
  /**
   * Loading more, the rows still to come: the placeholder, drawn after the
   * rows in hand as the rows are while the list loads - inert.
   */
  loadingMoreRows: T[];
  renderItem: SelectableCardListProps<T>["renderItem"];
  rowClassName?: string;
  getItemSwipeActions?: SelectableCardListProps<T>["getItemSwipeActions"];
  activeRowClassName?: string;
  stickyActiveRow?: boolean;
  stickyActiveRowInsetTop?: number;
  stickyActiveRowInsetBottom?: number;
  className?: string;
}) {
  const { size, rowInsetClassName } = useSelectableCardRowStyle();
  const { scrollRootRef, setScrollRef } =
    useSelectableCardListScrollRef<HTMLDivElement>();
  const {
    items,
    getItemKey,
    isItemActive,
    onRowActivate,
    getRowClassName,
    activeIndex,
    navDirection,
    registerRowRef,
    onRowHover,
    isFocusWithin,
  } = context;

  const rows = loadingMoreRows.length ? [...items, ...loadingMoreRows] : items;

  const {
    outerRef,
    innerRef,
    items: virtualItems,
    scrollToItem,
  } = useVirtual<HTMLDivElement, HTMLDivElement>({
    itemCount: rows.length,
    itemSize: variableItemHeight
      ? (index) => {
          const item = rows[index];
          if (!item) {
            return estimatedItemHeight;
          }
          const isActive = isItemActive?.(item, index) ?? false;
          return isActive ? activeItemHeight : estimatedItemHeight;
        }
      : estimatedItemHeight,
  });

  const setOuterRef = useCallback(
    (node: HTMLDivElement | null) => {
      setScrollRef(node);
      assignRef(outerRef, node);
    },
    [outerRef, setScrollRef],
  );

  // The virtualizer derives its window from a ResizeObserver, so a body that
  // mounts with items already in hand (e.g. a cache hit) renders zero rows on
  // its first commit and flashes empty. Measure in a layout effect (flushed
  // before paint) and fill the viewport from the size estimate until the
  // virtualizer takes over.
  const [initialPaintCount, setInitialPaintCount] = useState(0);
  const needsInitialPaint = virtualItems.length === 0 && rows.length > 0;
  useLayoutEffect(() => {
    if (!needsInitialPaint) {
      return;
    }
    const height = outerRef.current?.clientHeight ?? 0;
    setInitialPaintCount(Math.ceil(height / estimatedItemHeight) + 1);
  }, [estimatedItemHeight, needsInitialPaint, outerRef]);

  const renderedItems: Pick<
    (typeof virtualItems)[number],
    "index" | "start" | "measureRef"
  >[] = needsInitialPaint
    ? Array.from(
        { length: Math.min(initialPaintCount, rows.length) },
        (_, index) => ({
          index,
          start: index * estimatedItemHeight,
          measureRef: () => {},
        }),
      )
    : virtualItems;

  // Each drawn row's element, by index, to tell whether a row is in view.
  const rowNodes = useRef<Record<number, HTMLDivElement | null>>({});

  useEffect(() => {
    if (activeIndex < 0) return;
    const bufferIndex =
      navDirection === "down"
        ? Math.min(activeIndex + 1, items.length - 1)
        : navDirection === "up"
          ? Math.max(activeIndex - 1, 0)
          : activeIndex;
    // Asked only when there is somewhere to scroll to. The virtualizer marks
    // itself as scrolling to a row until it hears the list scroll, and lays
    // out none of the rows it measures meanwhile - so asked for a row already
    // in view, which moves nothing (the thread list, opened on the thread last
    // read), it left every row at the size it guessed, and they ran over each
    // other until something resized the list.
    const outer = outerRef.current;
    const row = rowNodes.current[bufferIndex];
    if (outer && outer.scrollHeight <= outer.clientHeight) return;
    if (outer && row?.isConnected) {
      const within = outer.getBoundingClientRect();
      const at = row.getBoundingClientRect();
      if (at.top >= within.top - 1 && at.bottom <= within.bottom + 1) return;
    }
    scrollToItem({ index: bufferIndex, smooth: false });
  }, [activeIndex, navDirection, scrollToItem]);

  return (
    <div
      ref={setOuterRef}
      data-selectable-card-list=""
      data-selectable-row-hover={rowHover}
      className={cn(
        SCROLL_CONTAINER_CLASS,
        !flush && SELECTABLE_LIST_INSET_CLASS[size],
        // The hairlines beside a hovered row step aside for the card it
        // becomes; a flush row becomes no card, and a list whose rows keep
        // still under the pointer has nothing to step aside for.
        rowHover !== "title" &&
          !flush &&
          SELECTABLE_CARD_ROW_DIVIDER_HOVER_CLASS,
        className,
      )}
    >
      <div ref={innerRef as any} className="relative">
        {renderedItems.map(({ index, start, measureRef }) => {
          const item = rows[index];
          if (!item) return null;
          const isPlaceholder = loading || index >= items.length;
          const key = isPlaceholder
            ? `__loading-${index}`
            : getItemKey(item, index);
          const isActive =
            !isPlaceholder && (isItemActive?.(item, index) ?? false);
          const prevItem = index > 0 ? items[index - 1] : undefined;
          const isPrevActive = prevItem
            ? (isItemActive?.(prevItem, index - 1) ?? false)
            : false;
          const hasDivider =
            showRowDividers &&
            (rowDividerPlacement === "overline" || index > 0);
          const showDivider =
            hasDivider &&
            (activeRowDividers === "keep" || (!isActive && !isPrevActive));

          return (
            <SelectableCardListRowShell
              key={key}
              index={index}
              isActive={isActive}
              stickyActiveRow={stickyActiveRow}
              stickyActiveRowInsetTop={stickyActiveRowInsetTop}
              stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
              registerRowRef={registerRowRef}
              outerRef={(node) => {
                rowNodes.current[index] = node;
                measureRef(node);
              }}
              wrapperStyle={{
                position: "absolute",
                top: start,
                left: 0,
                width: "100%",
                zIndex: isActive ? 1 : 0,
              }}
              swipeActions={
                isPlaceholder ? null : (getItemSwipeActions?.(item) ?? null)
              }
            >
              {hasDivider && (
                <div
                  data-selectable-row-divider=""
                  className={cn(
                    "h-px",
                    showDivider ? "bg-border" : "bg-transparent",
                    flush ? "ml-0 mr-0" : "ml-3 mr-3",
                    "[transition:background-color_120ms_ease]",
                  )}
                />
              )}
              <div
                data-selectable-row-body=""
                inert={isPlaceholder || undefined}
                onClickCapture={
                  isPlaceholder
                    ? undefined
                    : (event) =>
                        handleSelectableCardRowClickCapture(
                          event,
                          (clickCount) =>
                            onRowActivate(item, index, clickCount),
                        )
                }
                onMouseEnter={
                  isPlaceholder ? undefined : () => onRowHover(item, index)
                }
                className={cn(
                  "flex flex-col justify-start gap-1",
                  rowInsetClassName,
                  flush ? "ml-0 mr-0" : "ml-1 mr-1",
                  !variableItemHeight && "min-h-(--min-height)",
                  getRowClassName({
                    isActive,
                    className: cn(rowClassName, isActive && activeRowClassName),
                  }),
                )}
                style={
                  variableItemHeight
                    ? undefined
                    : ({
                        "--min-height": `${estimatedItemHeight}px`,
                      } as CSSProperties)
                }
              >
                {renderItem({
                  item,
                  index,
                  isActive,
                  isFocusWithin,
                  isLoading: isPlaceholder,
                })}
              </div>
            </SelectableCardListRowShell>
          );
        })}
      </div>
      {loadMore ? (
        <SelectableCardListLoadMoreSentinel
          loadMore={loadMore}
          scrollRootRef={scrollRootRef}
          flush={flush}
        />
      ) : null}
    </div>
  );
}

function SelectableCardListGroupedBody<T>({
  groups,
  context,
  estimatedItemHeight,
  variableItemHeight,
  showRowDividers,
  rowDividerPlacement,
  flush,
  rowHover,
  activeRowDividers,
  loading,
  loadMore,
  stickyGroupLabels = false,
  stickyGroupLabelHeight = 48,
  stickySectionLabelHeight = 44,
  stickyActiveRow = false,
  stickyActiveRowInsetTop = 0,
  stickyActiveRowInsetBottom = 0,
  onVisibleGroupChange,
  scrollToGroupId,
  scrollToGroupKey,
  groupItemsClassName,
  renderItem,
  rowClassName,
  getItemSwipeActions,
  activeRowClassName,
  className,
}: {
  groups: SelectableCardListGroup<T>[];
  context: SelectableListContext<T>;
  estimatedItemHeight: number;
  variableItemHeight: boolean;
  showRowDividers: boolean;
  rowDividerPlacement: "between" | "overline";
  flush: boolean;
  rowHover: SelectableRowHover;
  activeRowDividers: "hide" | "keep";
  /** The rows are the list's placeholder: it is still loading. */
  loading: boolean;
  loadMore?: SelectableCardListLoadMore;
  stickyGroupLabels?: boolean;
  stickyGroupLabelHeight?: number;
  stickySectionLabelHeight?: number;
  stickyActiveRow?: boolean;
  stickyActiveRowInsetTop?: number;
  stickyActiveRowInsetBottom?: number;
  onVisibleGroupChange?: (groupId: string) => void;
  scrollToGroupId?: string | null;
  scrollToGroupKey?: number;
  groupItemsClassName?: string;
  renderItem: SelectableCardListProps<T>["renderItem"];
  rowClassName?: string;
  getItemSwipeActions?: SelectableCardListProps<T>["getItemSwipeActions"];
  activeRowClassName?: string;
  className?: string;
}) {
  const { size } = useSelectableListStyle();
  const { scrollRootRef, setScrollRef } =
    useSelectableCardListScrollRef<HTMLDivElement>();
  const groupNodeRefs = useRef(new Map<string, HTMLElement>());
  const visibleGroupIdRef = useRef<string | null>(null);
  const {
    getItemKey,
    isItemActive,
    onRowActivate,
    getRowClassName,
    registerRowRef,
    onRowHover,
  } = context;

  const setGroupNodeRef = useCallback((groupId: string, node: unknown) => {
    if (node instanceof HTMLElement) {
      groupNodeRefs.current.set(groupId, node);
      return;
    }
    groupNodeRefs.current.delete(groupId);
  }, []);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || !onVisibleGroupChange || groups.length === 0) {
      return;
    }

    const updateVisibleGroup = () => {
      const rootTop = root.getBoundingClientRect().top;
      const threshold = stickyGroupLabels ? stickyGroupLabelHeight : 8;
      let current = groups[0]?.id ?? null;
      for (const group of groups) {
        const node = groupNodeRefs.current.get(group.id);
        if (!node) {
          continue;
        }
        const top = node.getBoundingClientRect().top - rootTop;
        if (top <= threshold + 1) {
          current = group.id;
        }
      }
      if (current && current !== visibleGroupIdRef.current) {
        visibleGroupIdRef.current = current;
        onVisibleGroupChange(current);
      }
    };

    updateVisibleGroup();
    root.addEventListener("scroll", updateVisibleGroup, { passive: true });
    return () => root.removeEventListener("scroll", updateVisibleGroup);
  }, [
    groups,
    onVisibleGroupChange,
    stickyGroupLabelHeight,
    stickyGroupLabels,
    scrollRootRef,
  ]);

  useEffect(() => {
    if (!scrollToGroupId) {
      return;
    }
    const root = scrollRootRef.current;
    const node = groupNodeRefs.current.get(scrollToGroupId);
    if (!root || !node) {
      return;
    }
    root.scrollTop +=
      node.getBoundingClientRect().top - root.getBoundingClientRect().top;
  }, [scrollToGroupId, scrollToGroupKey, scrollRootRef]);

  let globalIndex = 0;
  const stickyItemScrollInsetTop = stickyGroupLabels
    ? stickyGroupLabelHeight + stickySectionLabelHeight
    : 0;

  const renderGroupItemRows = (
    group: SelectableCardListGroup<T>,
    groupItems: T[],
  ) =>
    groupItems.map((item, itemIndexInGroup) => {
      const index = globalIndex;
      globalIndex += 1;
      const isActive = isItemActive?.(item, index) ?? false;
      const prevItem =
        itemIndexInGroup > 0 ? groupItems[itemIndexInGroup - 1] : undefined;
      const prevIndex = index - 1;
      const isPrevActive = prevItem
        ? (isItemActive?.(prevItem, prevIndex) ?? false)
        : false;
      const hasDivider =
        showRowDividers &&
        (rowDividerPlacement === "overline" || itemIndexInGroup > 0);
      const dividerVisible =
        activeRowDividers === "keep" || (!isActive && !isPrevActive);

      return (
        <SelectableCardListRow
          key={getItemKey(item, index)}
          item={item}
          index={index}
          isActive={isActive}
          isFocusWithin={context.isFocusWithin}
          group={group}
          estimatedItemHeight={estimatedItemHeight}
          variableItemHeight={variableItemHeight}
          renderItem={renderItem}
          rowClassName={rowClassName}
          getItemSwipeActions={getItemSwipeActions}
          activeRowClassName={activeRowClassName}
          hasDivider={hasDivider}
          dividerVisible={dividerVisible}
          flush={flush}
          scrollInsetTop={stickyItemScrollInsetTop}
          stickyActiveRow={stickyActiveRow}
          stickyActiveRowInsetTop={stickyActiveRowInsetTop}
          stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
          onRowActivate={onRowActivate}
          registerRowRef={registerRowRef}
          onRowHover={onRowHover}
          getRowClassName={getRowClassName}
          getItemKey={getItemKey}
          loading={loading}
        />
      );
    });

  return (
    <div
      ref={setScrollRef}
      data-selectable-card-list=""
      data-selectable-row-hover={rowHover}
      className={cn(
        SCROLL_CONTAINER_CLASS,
        !flush && !stickyGroupLabels && SELECTABLE_LIST_INSET_CLASS[size],
        // The hairlines beside a hovered row step aside for the card it
        // becomes; a flush row becomes no card, and a list whose rows keep
        // still under the pointer has nothing to step aside for.
        rowHover !== "title" &&
          !flush &&
          SELECTABLE_CARD_ROW_DIVIDER_HOVER_CLASS,
        className,
      )}
    >
      {groups.map((group) => {
        const sections =
          group.sections?.filter((section) => section.items.length > 0) ?? [];
        const hasSections = sections.length > 0;
        const flatItems = hasSections
          ? sections.flatMap((section) => section.items)
          : group.items;

        return (
          <div
            key={group.id}
            ref={(node) => setGroupNodeRef(group.id, node)}
            data-group-id={group.id}
          >
            {group.label || group.labelContent ? (
              <SelectableCardListGroupLabel
                label={group.label}
                labelContent={group.labelContent}
                labelEnd={group.labelEnd}
                subtitle={group.subtitle}
                stickyGroupLabels={stickyGroupLabels}
                stickyGroupLabelHeight={stickyGroupLabelHeight}
                stickySectionLabelHeight={stickySectionLabelHeight}
                flush={flush}
                hasSections={hasSections}
              />
            ) : null}
            {hasSections ? (
              sections.map((section) => (
                <div key={section.id} data-section-id={section.id}>
                  <SelectableCardListSectionLabel
                    label={section.label}
                    labelContent={section.labelContent}
                    subtitle={section.subtitle}
                    stickyGroupLabels={stickyGroupLabels}
                    stickyGroupLabelHeight={stickyGroupLabelHeight}
                    stickySectionLabelHeight={stickySectionLabelHeight}
                  />
                  <div
                    className={cn(
                      GROUP_ITEMS_CONTAINER_CLASS,
                      groupItemsClassName,
                    )}
                  >
                    {renderGroupItemRows(group, section.items)}
                  </div>
                </div>
              ))
            ) : (
              <div
                className={cn(GROUP_ITEMS_CONTAINER_CLASS, groupItemsClassName)}
              >
                {flatItems.length === 0 ? (
                  <Typography
                    level="body-sm"
                    className="pl-1.5 pr-1.5 pt-1.5 pb-1.5 text-muted-foreground/80"
                  >
                    {group.emptyLabel ?? "None"}
                  </Typography>
                ) : (
                  renderGroupItemRows(group, flatItems)
                )}
              </div>
            )}
          </div>
        );
      })}
      {loadMore ? (
        <SelectableCardListLoadMoreSentinel
          loadMore={loadMore}
          scrollRootRef={scrollRootRef}
          flush={flush}
        />
      ) : null}
    </div>
  );
}

export type SelectableCardListSearchAlign =
  | "grouped-cards"
  | "inset-rows"
  | "flush"
  /**
   * A search on a bar's row, beside its controls: no box of its own - the bar
   * is the box - its words at the row's own inset, the bar's ``h-8``.
   */
  | "field";

const SEARCH_ALIGN_CONTAINER_CLASS = {
  "grouped-cards": "pl-2 pr-2",
  "inset-rows": "pl-2 pr-2",
  flush: "pl-0 pr-0",
  field: "",
} satisfies Record<SelectableCardListSearchAlign, string>;

const SEARCH_ALIGN_INPUT_CLASS = {
  "grouped-cards": "",
  "inset-rows": "",
  flush: "pl-4 pr-4",
  field: "",
} satisfies Record<SelectableCardListSearchAlign, string>;

/** Lines search placeholder text up with ``SelectableListItemRow`` titles. */
export function SelectableCardListSearchField({
  placeholder,
  value,
  onChange,
  onKeyDown,
  inputRef,
  align = "inset-rows",
  containerClassName,
  inputTestId,
  disabled = false,
  endDecorator,
  trailing,
  leading,
  hasGlass = true,
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  align?: SelectableCardListSearchAlign;
  containerClassName?: string;
  inputTestId?: string;
  disabled?: boolean;
  endDecorator?: React.ReactNode;
  /** Sits beside the input, not inside its end decorator. */
  trailing?: React.ReactNode;
  /** Inside the input, after its glass and before what is typed: what the search is narrowed to. */
  leading?: React.ReactNode;
  /** A glass before it all - not where its words say what it searches for. */
  hasGlass?: boolean;
}) {
  const inset = SEARCH_ALIGN_CONTAINER_CLASS[align];
  const inputInset = SEARCH_ALIGN_INPUT_CLASS[align];

  const input = (
    <InputGroup
      className={
        align === "field"
          ? // Among a bar's controls: no edge, no ground - what it is
            // narrowed to on its one line.
            "border-0 bg-transparent shadow-none dark:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0 h-8 flex-1 w-full min-w-0 px-0"
          : cn(
              "border-0 bg-transparent shadow-none dark:bg-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0",
              // Its inset is its own - the align's - and what is before or
              // after the words sits inside it, adding none: an addon is
              // there only with something in it.
              "flex-1 w-full min-w-0 px-3",
              trailing ? "pl-0 pr-0" : inputInset,
            )
      }
    >
      {hasGlass || leading ? (
        // What it is narrowed to wraps in its own place, close together - or,
        // a field on one line, goes short there.
        <InputGroupAddon
          className={cn(
            "pl-0 gap-0 justify-start",
            // A field's, on its one line: its words cut short before what is
            // typed gives a pixel - shrunk ten thousand times as fast, it is at
            // its least, its marks and a letter of each word, first - and
            // never under that.
            align === "field" ? "flex-nowrap shrink-[10000]" : "flex-wrap",
          )}
        >
          {hasGlass ? (
            <Icon
              ReactIcon={icons.search}
              size="md"
              style={{ marginLeft: 2, marginRight: 2 }}
            />
          ) : null}
          {leading}
        </InputGroupAddon>
      ) : null}
      <InputGroupInput
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        ref={inputRef}
        className={cn(
          cn(
            "pl-0 pr-0",
            // Room to type, however many chips there are: what it is
            // narrowed to wraps in its own place before it instead - or, a
            // field on one line, the words give way to its marks.
            leading &&
              (align === "field"
                ? "flex-[1_1_160px] min-w-0"
                : "flex-[1_1_160px] min-w-40"),
          ),
        )}
        {...(inputTestId ? { "data-testid": inputTestId } : {})}
      />
      {endDecorator ? (
        <InputGroupAddon align="inline-end" className="pr-0 gap-0">
          <div className="flex items-center shrink-0 pr-1.5">
            {endDecorator}
          </div>
        </InputGroupAddon>
      ) : null}
    </InputGroup>
  );

  if (trailing) {
    return (
      <div
        className={cn(
          "w-full flex items-center gap-2",
          inset,
          inputInset,
          containerClassName,
        )}
      >
        {input}
        {/* A row: what trails may be several things (tab toggles and a
            button), set side by side. */}
        <div className="shrink-0 flex items-center gap-2">{trailing}</div>
      </div>
    );
  }

  return (
    <div className={cn("w-full flex items-center", inset, containerClassName)}>
      {input}
    </div>
  );
}

export function SelectableCardList<T>({
  items: itemsProp,
  groups,
  estimatedItemHeight = DEFAULT_ESTIMATED_ITEM_HEIGHT,
  activeItemHeight,
  variableItemHeight = false,
  disableVirtualization = false,
  showRowDividers = true,
  rowDividerPlacement = "between",
  rowHover = "surface",
  loading = false,
  loadingItemCount = DEFAULT_LOADING_ITEM_COUNT,
  loadMore,
  stickyGroupLabels = false,
  stickyGroupLabelHeight = 48,
  stickySectionLabelHeight = 44,
  stickyActiveRow = false,
  animatesRemovals = false,
  isFillingIn = false,
  listKey,
  stickyActiveRowInsetTop,
  stickyActiveRowInsetBottom = 0,
  onVisibleGroupChange,
  scrollToGroupId,
  scrollToGroupKey,
  groupItemsClassName,
  renderItem: renderItemProp,
  renderItemDetails,
  isItemSelected,
  placeholderItem,
  rowClassName,
  getItemSwipeActions,
  activeRowClassName,
  className,
  onItemClick,
  onItemNavigate,
  onItemHover,
  onItemSpace,
  "data-testid": dataTestid,
  ...baseProps
}: SelectableCardListProps<T>) {
  // ``flush`` runs the rows edge to edge, and keeps the hairlines beside the
  // selected one - it draws nothing round itself that they would double. A
  // phone's list is always flush, and a narrow window's: it is the screen's
  // width, and an inset round it only narrows rows already short of room.
  const { isMobile, isNarrow } = useDevice();
  const layout = baseProps.layout ?? SELECTABLE_LIST_STYLE_DEFAULT.layout;
  const flush = layout === "flush" || isNarrow;
  const activeRowDividers: "hide" | "keep" = flush ? "keep" : "hide";
  const resolvedActiveItemHeight = activeItemHeight ?? estimatedItemHeight;
  const resolvedGroups = useMemo(
    () =>
      groups ??
      (itemsProp
        ? [
            {
              id: "_default",
              label: "",
              items: itemsProp,
              selectionMode: "single" as const,
            },
          ]
        : []),
    [groups, itemsProp],
  );

  const items = useMemo(
    () =>
      resolvedGroups.flatMap((group) =>
        group.sections && group.sections.length > 0
          ? group.sections.flatMap((section) => section.items)
          : group.items,
      ),
    [resolvedGroups],
  );

  const groupByIndex = useMemo(() => {
    const map = new Map<number, SelectableCardListGroup<T>>();
    let index = 0;
    for (const group of resolvedGroups) {
      const groupItems =
        group.sections && group.sections.length > 0
          ? group.sections.flatMap((section) => section.items)
          : group.items;
      for (let itemIndex = 0; itemIndex < groupItems.length; itemIndex += 1) {
        map.set(index, group);
        index += 1;
      }
    }
    return map;
  }, [resolvedGroups]);

  const handleItemSpace = useCallback(
    (item: T, index: number, event: KeyboardEvent) => {
      const group = groupByIndex.get(index);
      if (onItemSpace) {
        onItemSpace(item, index, event);
        return;
      }
      if (group?.selectionMode === "multi") {
        return;
      }
      onItemClick?.(item, index);
    },
    [groupByIndex, onItemClick, onItemSpace],
  );

  const useGroupedLayout = groups != null;
  const hasStickySections = resolvedGroups.some(
    (group) => (group.sections?.length ?? 0) > 0,
  );
  const resolvedStickyActiveRowInsetTop =
    stickyActiveRowInsetTop ??
    (stickyGroupLabels
      ? stickyGroupLabelHeight +
        (hasStickySections ? stickySectionLabelHeight : 0)
      : 0);
  // Loading, the rows are the placeholder - drawn by ``renderItem`` as any row
  // is, in the same body and card - and inert.
  const isLoading = loading && items.length === 0;
  // A phone draws no chosen row. On a computer the chosen row is the one the
  // keys are on and the panel beside it shows - in a narrow window too, where
  // the keys still are; a phone shows the list or what a row opens, never
  // both, and is tapped, not keyed - so a row drawn as chosen there is one
  // that was opened a while ago, and looks picked when nothing is. The list
  // has none there; the row is still told it is the one, for what it offers
  // only then (a suggestion's Reject and View).
  const renderItem: typeof renderItemProp =
    isMobile && !isLoading && baseProps.isItemActive
      ? (args) =>
          renderItemProp({
            ...args,
            isActive: baseProps.isItemActive!(args.item, args.index),
          })
      : renderItemProp;
  const placeholderRows = useMemo(
    () => Array.from({ length: loadingItemCount }, () => placeholderItem),
    [loadingItemCount, placeholderItem],
  );
  // Loading more, as many placeholder rows as the page brings, after the rows
  // in hand.
  const loadingMoreCount =
    !isLoading && loadMore?.loading
      ? (loadMore.loadingItemCount ?? loadingItemCount)
      : 0;
  const loadingMoreRows = useMemo(
    () => Array.from({ length: loadingMoreCount }, () => placeholderItem),
    [loadingMoreCount, placeholderItem],
  );
  const placeholderGroups = useMemo(
    () => [
      {
        id: "_loading",
        label: "",
        items: placeholderRows,
        selectionMode: "single" as const,
      },
    ],
    [placeholderRows],
  );

  return (
    <SelectableList
      {...baseProps}
      {...(isMobile ? { isItemActive: undefined } : {})}
      {...(isLoading
        ? {
            getItemKey: (_item: T, index: number) => `__loading-${index}`,
            isItemActive: undefined,
            onItemEnter: undefined,
            scrollToActiveKey: null,
            enableKeyboardNav: false,
          }
        : {})}
      items={isLoading ? placeholderRows : items}
      onItemClick={isLoading ? undefined : onItemClick}
      onItemNavigate={isLoading ? undefined : onItemNavigate}
      onItemHover={isLoading ? undefined : onItemHover}
      onItemSpace={isLoading ? undefined : handleItemSpace}
      scrollInsetTop={stickyGroupLabels ? stickyGroupLabelHeight : 0}
      stickyActiveRow={stickyActiveRow}
      data-testid={dataTestid}
      emptyState={
        isLoading || (useGroupedLayout && items.length === 0)
          ? undefined
          : baseProps.emptyState
      }
    >
      {(context) =>
        useGroupedLayout ? (
          <SelectableCardListGroupedBody
            rowHover={rowHover}
            activeRowDividers={activeRowDividers}
            groups={isLoading ? placeholderGroups : resolvedGroups}
            context={context}
            estimatedItemHeight={estimatedItemHeight}
            variableItemHeight={variableItemHeight}
            showRowDividers={showRowDividers}
            rowDividerPlacement={rowDividerPlacement}
            flush={flush}
            loading={isLoading}
            loadMore={isLoading ? undefined : loadMore}
            stickyGroupLabels={stickyGroupLabels}
            stickyGroupLabelHeight={stickyGroupLabelHeight}
            stickySectionLabelHeight={stickySectionLabelHeight}
            stickyActiveRow={stickyActiveRow}
            stickyActiveRowInsetTop={resolvedStickyActiveRowInsetTop}
            stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
            onVisibleGroupChange={onVisibleGroupChange}
            scrollToGroupId={scrollToGroupId}
            scrollToGroupKey={scrollToGroupKey}
            groupItemsClassName={groupItemsClassName}
            renderItem={renderItem}
            rowClassName={rowClassName}
            getItemSwipeActions={getItemSwipeActions}
            activeRowClassName={activeRowClassName}
            className={className}
          />
        ) : disableVirtualization ? (
          <SelectableCardListDirectBody
            rowHover={rowHover}
            activeRowDividers={activeRowDividers}
            context={context}
            estimatedItemHeight={estimatedItemHeight}
            variableItemHeight={variableItemHeight}
            showRowDividers={showRowDividers}
            rowDividerPlacement={rowDividerPlacement}
            flush={flush}
            loading={isLoading}
            loadMore={isLoading ? undefined : loadMore}
            loadingMoreRows={loadingMoreRows}
            stickyActiveRow={stickyActiveRow}
            animatesRemovals={animatesRemovals}
            isFillingIn={isFillingIn}
            listKey={listKey}
            stickyActiveRowInsetTop={resolvedStickyActiveRowInsetTop}
            stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
            groupItemsClassName={groupItemsClassName}
            renderItem={renderItem}
            renderItemDetails={renderItemDetails}
            isItemSelected={isItemSelected}
            rowClassName={rowClassName}
            getItemSwipeActions={getItemSwipeActions}
            activeRowClassName={activeRowClassName}
            className={className}
          />
        ) : (
          <SelectableCardListBody
            // A fresh virtualizer for the real rows: the one that measured the
            // placeholders keeps their sizes by index, and laid the rows that
            // replaced them over one another.
            key={isLoading ? "placeholders" : "rows"}
            rowHover={rowHover}
            activeRowDividers={activeRowDividers}
            context={context}
            estimatedItemHeight={estimatedItemHeight}
            activeItemHeight={resolvedActiveItemHeight}
            variableItemHeight={variableItemHeight}
            showRowDividers={showRowDividers}
            rowDividerPlacement={rowDividerPlacement}
            flush={flush}
            loading={isLoading}
            loadMore={isLoading ? undefined : loadMore}
            loadingMoreRows={loadingMoreRows}
            stickyActiveRow={stickyActiveRow}
            stickyActiveRowInsetTop={resolvedStickyActiveRowInsetTop}
            stickyActiveRowInsetBottom={stickyActiveRowInsetBottom}
            renderItem={renderItem}
            rowClassName={rowClassName}
            getItemSwipeActions={getItemSwipeActions}
            activeRowClassName={activeRowClassName}
            className={className}
          />
        )
      }
    </SelectableList>
  );
}
